import { useFormikContext } from "formik";
import React, { FC, useEffect, useState } from "react";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import ReaderColumns from "../../../models/ColumnDefinitions/ReaderColumns";
import { ListQuery } from "../../../models/ListQuery";
import ReaderSearch from "../../../models/search/ReaderSearch";
import TableTab from "../../tabs/table-tab";
import { DeviceImprox } from "../../../models/DeviceImprox";
import { getDeviceImprox } from "../../../services/deviceImproxService";
import { getDeviceForSetting } from "../../../services/deviceInputService";
import { removeColumns } from "../../../models/utilities/columnHelper";
import { DEVICE_CATEGORIES } from "../../../constants/device-constants";
import { appendFilter } from "../../../utilities/filters";
import { Name } from "@/models/Name";

interface PresetTriggeredByProps {
    onNext: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onBack: (event: React.MouseEvent<HTMLButtonElement>) => void;
    setSideBarStepDescription: (newDescription: string) => void;
    presetSettingId: string;
}

const PresetTriggeredBy: FC<PresetTriggeredByProps> = ({ onNext, onBack, setSideBarStepDescription, presetSettingId }) => {
    const { translate } = useLocale();
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const {setFieldValue} = useFormikContext<any>();
    const [originalTriggeredByIds, setOriginalTriggeredByIds] = useState<string[]>([]);
    const [selectRows, setSelectRows] = useState<Name[]>([]);

    useEffect(() => {
        getDeviceLinkedToSetting(presetSettingId);
    }, [presetSettingId]);

    const getDeviceLinkedToSetting = async (settingId: string) => {
        if (settingId) {
            try {
                const devices = await getDeviceForSetting(settingId);
                const ids = devices.rows.map(device => device.value);
                setOriginalTriggeredByIds(ids);
                setSelectedIds(ids);
                setSelectRows(devices.rows.map(device => ({ value: device.value, label: device.label })));
            } catch (error) {
                console.error("Error fetching devices for setting:", error);
            }
        }
    }

    const onLocalNext = (event: React.MouseEvent<HTMLButtonElement>) => {
        setFieldValue('triggeredByIds', selectedIds);
        setFieldValue('triggeredByNames', selectRows.map(row => row.label));
        setFieldValue('triggeredByToRemove', originalTriggeredByIds.filter(id => !selectedIds.includes(id)));
        setSideBarStepDescription(translate.byKeyFormatted(TranslationKeys.selectedWithCount, { count: selectedIds.length }));
        onNext(event);
    }

    const onRowSelect = (readers: DeviceImprox[]) => {
        const names: Name[] = readers.map((reader): Name => {
            return {
                value: reader.id.toString(),
                label: reader.name
            }
        });

        setSelectRows(names);
    }

    const readerColumns = removeColumns(ReaderColumns, ['type', 'site', 'devReaderSerial', 'site', 'controller', 'door', 'preset', 'musteringPointLabel', 'devLicense']);
    return <div className="flex flex-col pt-3 pr-3 h-full relative">
        <div className="flex flex-col overflow-y-auto" style={{ height: '85%' }}>
            <div className="flex flex-col pt-3">
                <TableContextProvider
                    dataMethod={(listParameters?: ListQuery) => getDeviceImprox({ ...listParameters, filters: appendFilter(`${DEVICE_CATEGORIES.READER},${DEVICE_CATEGORIES.INPUT_OUTPUT}`, "Category", "unitType.utCategory", listParameters?.filters ?? []) }) }
                    tableDescriptor={readerColumns}
                    searchDescriptor={ReaderSearch}
                    useFilter
                    useSort
                    usePagination
                    useSearch
                    useTable>
                    <TableTab
                        title={translate.byKey(TranslationKeys.triggeredBy)}
                        selectRows={true}
                        selectedIds={selectedIds}
                        setSelectedIds={setSelectedIds}
                        setSelectedRows={onRowSelect}
                        cyName="tbl_triggeredBy" />
                </TableContextProvider >
            </div>
        </div>
        <div className="flex flex-row justify-end absolute bottom-0 right-0 pr-3 pb-3 ">
            <div className="flex flex-row mt-4">
                <button
                    className={`secondary secondary-inverse py-1 px-5`}
                    type="button"
                    onClick={onBack}>{translate.byKey(TranslationKeys.back)}</button>
            </div>
            <div className="flex flex-row justify-end mt-4 pl-3">
                <button
                    className={`py-1 px-5 secondary secondary-enabled`}
                    type='button'
                    onClick={onLocalNext}>{translate.byKey(TranslationKeys.next)}</button>
            </div>
        </div>
    </div>
}

export default PresetTriggeredBy;